import React from 'react';

import { Stack, createStyles, useMantineTheme } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { ResourceViewerWrapper } from 'components/ResourceViewerWrapper/ResourceViewerWrapper';
import { PdfViewer } from 'pages/PdfViewer/PdfViewer';
import { useParams } from 'react-router';
import { ResourceService } from 'services/resource-service';
import { DOWNLOAD_FILE_QUERY_KEY, RESOURCE_QUERY_KEY } from 'shared/constants/query-keys';

const useStyles = createStyles(() => ({
  pdfViewerContainer: {
    padding: '44px 12px',
    flexDirection: 'column',
    justifyContent: 'center',
    flex: 1
  }
}));

const PDF_PAGE_WIDTH = 800;

export function PdfViewerPage() {
  const theme = useMantineTheme();
  const { resourceId } = useParams();
  const { classes } = useStyles();

  if (!resourceId) return <ErrorAlert />;

  const fileQuery = useQuery({
    queryKey: [DOWNLOAD_FILE_QUERY_KEY, { resourceId }],
    queryFn: () => ResourceService.downloadResourceFile(resourceId),
    refetchOnWindowFocus: false
  });

  const resourceQuery = useQuery({
    queryKey: [RESOURCE_QUERY_KEY, { resourceId }],
    queryFn: () => ResourceService.getResourceInfo(resourceId),
    refetchOnWindowFocus: false
  });

  const queriesAreLoading = fileQuery.isLoading || resourceQuery.isLoading;
  const queriesHaveEmptyData = !fileQuery.data || !resourceQuery.data;
  const queriesHaveErrors = fileQuery.isError || resourceQuery.isError;

  if (queriesAreLoading || queriesHaveEmptyData) return <FullScreenLoader />;
  if (queriesHaveErrors) return <ErrorAlert />;

  return (
    <ResourceViewerWrapper
      resourceId={resourceId}
      fileName={resourceQuery.data?.fileName}
      backgroundColor={theme.colors.gray[0]}>
      <Stack className={classes.pdfViewerContainer}>
        <PdfViewer fileData={fileQuery.data} displayPageWidth={PDF_PAGE_WIDTH} />
      </Stack>
    </ResourceViewerWrapper>
  );
}
